import { getAwardShowResultsStatus } from "./utils";
import { isPartyLocked } from "./validation";
import { isPartyBallotComplete } from "./tie-breakers";
import {
  getPrimaryActionHref,
  type PartyDashboardPrimaryAction,
} from "./party-dashboard";

export interface PartyListSnapshot {
  id: string;
  votingClosesAt: Date | string | null;
  isManuallyLocked: boolean;
  announcedResultsCount: number;
  totalCategories: number;
  userVoteCount: number;
}

function closingDate(votingClosesAt: Date | string | null): Date {
  if (votingClosesAt == null) {
    return new Date(8.64e15);
  }
  return votingClosesAt instanceof Date
    ? votingClosesAt
    : new Date(votingClosesAt);
}

export function isPartyListActive(
  party: Pick<PartyListSnapshot, "announcedResultsCount" | "totalCategories">,
): boolean {
  return (
    getAwardShowResultsStatus(
      party.announcedResultsCount,
      party.totalCategories,
    ) !== "complete"
  );
}

export function isPartyVotingClosed(
  party: Pick<PartyListSnapshot, "isManuallyLocked" | "votingClosesAt">,
  nowMs: number,
): boolean {
  return isPartyLocked(
    party.isManuallyLocked,
    closingDate(party.votingClosesAt),
    new Date(nowMs),
  );
}

export function isPartyAwaitingResults(
  party: PartyListSnapshot,
  nowMs: number,
): boolean {
  return isPartyVotingClosed(party, nowMs) && isPartyListActive(party);
}

function sortBySoonestClose(a: PartyListSnapshot, b: PartyListSnapshot): number {
  return closingDate(a.votingClosesAt).getTime() - closingDate(b.votingClosesAt).getTime();
}

export function pickUpNextParty<T extends PartyListSnapshot>(
  parties: T[],
  nowMs: number,
): T | null {
  const active = parties.filter(isPartyListActive);
  if (active.length === 0) {
    return null;
  }

  const open = active.filter((party) => !isPartyVotingClosed(party, nowMs));
  if (open.length > 0) {
    return [...open].sort(sortBySoonestClose)[0] ?? null;
  }

  const live = active.filter(
    (party) =>
      getAwardShowResultsStatus(
        party.announcedResultsCount,
        party.totalCategories,
      ) === "in-progress",
  );
  if (live.length > 0) {
    return [...live].sort(sortBySoonestClose)[0] ?? null;
  }

  return [...active].sort(sortBySoonestClose)[0] ?? null;
}

export interface UpNextPresentation {
  href: string;
  label: string;
  statusLabel: string;
}

function getOpenPredictionsPresentation(
  party: PartyListSnapshot,
): UpNextPresentation {
  const voteHref = `/parties/${party.id}/vote`;
  const ballotComplete = isPartyBallotComplete({
    userVoteCount: party.userVoteCount,
    totalCategories: party.totalCategories,
  });

  if (ballotComplete) {
    return {
      href: voteHref,
      label: "Review predictions",
      statusLabel: "Picks submitted",
    };
  }

  if (party.userVoteCount > 0) {
    return {
      href: voteHref,
      label: "Finish predictions",
      statusLabel: "Predictions open",
    };
  }

  return {
    href: voteHref,
    label: "Enter predictions",
    statusLabel: "Predictions open",
  };
}

const UP_NEXT_PRIMARY_LABELS: Record<
  Exclude<PartyDashboardPrimaryAction, "vote">,
  string
> = {
  invite: "Invite friends",
  nudge: "See who hasn't voted",
  compare_picks: "Compare picks",
  leaderboard: "Watch results",
  final_standings: "View recap",
  chat: "Open chat",
};

export function getUpNextPresentationForPrimaryAction(
  party: PartyListSnapshot,
  nowMs: number,
  primaryAction: PartyDashboardPrimaryAction,
): UpNextPresentation {
  const { statusLabel } = getUpNextPresentation(party, nowMs);
  const href = getPrimaryActionHref(primaryAction, party.id);

  if (primaryAction === "vote") {
    return {
      href,
      label: getOpenPredictionsPresentation(party).label,
      statusLabel,
    };
  }

  return {
    href,
    label: UP_NEXT_PRIMARY_LABELS[primaryAction],
    statusLabel,
  };
}

export function getUpNextPresentation(
  party: PartyListSnapshot,
  nowMs: number,
): UpNextPresentation {
  const resultsStatus = getAwardShowResultsStatus(
    party.announcedResultsCount,
    party.totalCategories,
  );
  const locked = isPartyVotingClosed(party, nowMs);

  if (!locked && resultsStatus !== "complete") {
    return getOpenPredictionsPresentation(party);
  }

  if (resultsStatus === "in-progress") {
    return {
      href: `/parties/${party.id}/leaderboard`,
      label: "Watch results",
      statusLabel: "Results live",
    };
  }

  if (locked && resultsStatus !== "complete") {
    return {
      href: `/parties/${party.id}`,
      label: "Open party",
      statusLabel: "Awaiting results",
    };
  }

  return {
    href: `/parties/${party.id}/leaderboard`,
    label: "View recap",
    statusLabel: "Wrapped",
  };
}

export function formatPartyCountSummary(
  partyCount: number,
  activeCount: number,
): string {
  const partyWord = partyCount === 1 ? "party" : "parties";
  return `${partyCount} ${partyWord} · ${activeCount} active`;
}

export function pickUpcomingShowNames(
  shows: { name: string; startTime: Date | string | null }[],
  nowMs: number,
): string[] {
  return shows
    .filter((show) => {
      if (!show.startTime) {
        return true;
      }
      return new Date(show.startTime).getTime() >= nowMs;
    })
    .map((show) => show.name);
}

export function formatBrowseShowsBlurb(showNames: string[]): string {
  if (showNames.length === 0) {
    return "Find an award show and start a party.";
  }

  if (showNames.length === 1) {
    return `${showNames[0]} is open for new parties this season.`;
  }

  if (showNames.length === 2) {
    return `${showNames[0]} and ${showNames[1]} are open for new parties this season.`;
  }

  const remaining = showNames.length - 2;
  return `${showNames[0]}, ${showNames[1]} and ${remaining} more show${remaining === 1 ? "" : "s"} open for new parties this season.`;
}
